
import React from "react";
import { CardContent, CardFooter } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Small } from "@/components/ui/typography";
import AuthDivider from "./AuthDivider";
import SocialLoginButton from "./SocialLoginButton";
import AuthButton from "./AuthButton";
import { useToast } from "@/hooks/use-toast";
import { useAuthForm } from "@/context/AuthFormContext";

// Create a Label component that uses Typography internally
const Label = ({ htmlFor, children }: { htmlFor: string, children: React.ReactNode }) => (
  <Small weight="medium" className="text-mono-seasalt block mb-1.5">
    {children}
  </Small>
);

type SignupFormProps = {
  email: string;
  setEmail: (email: string) => void;
  password: string;
  setPassword: (password: string) => void;
  loading: boolean;
  googleLoading: boolean;
  handleSignup: (e: React.FormEvent) => Promise<void>;
  handleGoogleLogin: () => Promise<void>;
};

const SignupForm = ({
  email,
  setEmail,
  password,
  setPassword,
  loading,
  googleLoading,
  handleSignup,
  handleGoogleLogin
}: SignupFormProps) => {
  const { toast } = useToast();
  const { showPassword, setShowPassword } = useAuthForm();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !password) {
      return;
    }
    
    if (password.length < 6) {
      toast({
        title: "Password too short",
        description: "Password must be at least 6 characters long",
        variant: "destructive"
      });
      return;
    }
    
    try {
      await handleSignup(e);
    } catch (error) {
      console.error("Signup error:", error);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col">
      <CardContent className="space-y-4 pt-4 pb-4">
        <div className="space-y-2">
          <Label htmlFor="signup-email">Email</Label>
          <Input
            id="signup-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@gmail.com"
            required
            className="bg-mono-onyx border-mono-slateGray/30 transition-colors"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="signup-password">Password</Label>
          <PasswordInput
            id="signup-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={6}
            placeholder="••••••••"
            required
            className="bg-mono-onyx border-mono-slateGray/30 transition-colors"
            showPasswordState={[showPassword, setShowPassword]}
          />
          <div className="flex justify-start h-6">
            <Small className="text-mono-frenchGray text-xs">
              Password must be at least 6 characters long
            </Small>
          </div>
        </div>
      </CardContent>
      
      <CardFooter className="flex flex-col gap-4 pt-4 pb-8">
        <AuthButton 
          type="submit" 
          loading={loading}
        >
          {loading ? "Creating account..." : "Sign up"}
        </AuthButton>
        
        <AuthDivider />
        
        <SocialLoginButton 
          provider="google" 
          onClick={handleGoogleLogin} 
          loading={googleLoading} 
        />
      </CardFooter>
    </form>
  );
};

export default SignupForm;
